import { useState } from "react";
import {
    AlertCircle,
    CheckCircle,
    Mail,
    MessageSquare,
    Phone,
    Send,
} from "lucide-react";

import Header from "../components/Header";

const WEB3FORMS_ACCESS_KEY =
    "d7eec4b0-bb41-4537-9a12-e1169952796f";

const INITIAL_FORM_DATA = {
    name: "",
    email: "",
    message: "",
};

export const Contact = () => {
    const [formData, setFormData] =
        useState(INITIAL_FORM_DATA);
    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] =
        useState("");

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (status === "loading") {
            return;
        }

        const name = formData.name.trim();
        const email = formData.email
            .trim()
            .toLowerCase();
        const message = formData.message.trim();

        if (!name || !email || !message) {
            setStatus("error");
            setErrorMessage(
                "Please fill out your name, email, and message."
            );
            return;
        }

        const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            setStatus("error");
            setErrorMessage(
                "Please enter a valid email address."
            );
            return;
        }

        try {
            setStatus("loading");
            setErrorMessage("");

            const submission = new FormData();

            submission.append(
                "access_key",
                WEB3FORMS_ACCESS_KEY
            );

            submission.append(
                "subject",
                "New Contact Form - Call It Out"
            );

            submission.append("name", name);
            submission.append("email", email);
            submission.append("message", message);

            submission.append(
                "Website",
                "Call It Out"
            );

            submission.append(
                "Source",
                "Contact Page"
            );

            const response = await fetch(
                "https://api.web3forms.com/submit",
                {
                    method: "POST",
                    body: submission,
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "Web3Forms could not submit the form."
                );
            }

            setStatus("success");
            setFormData(INITIAL_FORM_DATA);
        } catch (error) {
            console.error(
                "Contact form error:",
                error
            );

            setStatus("error");
            setErrorMessage(
                "Something went wrong. Please try again or email us directly."
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#f8f4ea] text-black">
            <Header />

            <main className="relative overflow-hidden border-t-[4px] border-black">
                {/* Background texture */}
                <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(0,0,0,0.08)_1px,transparent_1px)] [background-size:14px_14px] opacity-70" />

                {/* Decorative backgrounds */}
                <div
                    className="absolute left-0 top-0 h-[620px] w-full bg-cover bg-center opacity-80"
                    style={{
                        backgroundImage:
                            "url('/assets/HeroBackground.png')",
                    }}
                />

                <div
                    className="absolute bottom-0 right-0 h-[520px] w-full bg-cover bg-center opacity-10"
                    style={{
                        backgroundImage:
                            "url('/assets/HeroBackground.png')",
                    }}
                />

                <section className="relative mx-auto max-w-5xl px-6 pb-12 pt-20 md:pb-16 md:pt-24 lg:px-8">
                    {/* Page heading */}
                    <div className="mx-auto mb-10 max-w-3xl text-center">
                        <div className="inline-flex items-center gap-2 rounded-full border-[3px] border-black bg-[#ffbc00] px-5 py-2 text-xs font-black uppercase tracking-wider shadow-[4px_4px_0_#000]">
                            <MessageSquare
                                size={17}
                                strokeWidth={3}
                            />
                            Contact Us
                        </div>

                        <h1 className="mt-7 text-4xl font-black uppercase leading-[0.95] tracking-[-0.04em] md:text-6xl">
                            Let&apos;s Connect
                        </h1>

                        <p className="mx-auto mt-5 max-w-2xl text-base font-bold leading-relaxed md:text-lg">
                            Have a question, comment, or idea?
                            Send us a message and we&apos;ll get
                            back to you.
                        </p>
                    </div>

                    <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
                        {/* Contact details */}
                        <aside className="space-y-5">
                            <ContactInfoCard
                                icon={Phone}
                                label="Phone"
                                value="917-302-5086"
                                href="tel:917-302-5086"
                                background="bg-[#ffbc00]"
                            />

                            <ContactInfoCard
                                icon={Mail}
                                label="Email"
                                value="playtohealgames@gmail.com"
                                href="mailto:playtohealgames@gmail.com"
                                background="bg-red-500 text-white"
                            />

                            <div className="rounded-[24px] border-[4px] border-black bg-white p-6 shadow-[7px_7px_0_#000]">
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-500">
                                    We&apos;d Love to Hear From You
                                </p>

                                <h2 className="mt-3 text-2xl font-black uppercase leading-tight">
                                    How Can We Help?
                                </h2>

                                <p className="mt-4 text-sm font-semibold leading-relaxed text-black/70">
                                    Contact us with questions about
                                    the game, facilitator resources,
                                    partnerships, workshops, or
                                    anything else you would like to
                                    discuss.
                                </p>
                            </div>
                        </aside>

                        {/* Contact form */}
                        <div className="rounded-[24px] border-[4px] border-black bg-white p-5 shadow-[7px_7px_0_#000] sm:p-7 md:p-9">
                            <div className="mb-7">
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-500">
                                    Send a Message
                                </p>

                                <h2 className="mt-2 text-3xl font-black uppercase leading-tight">
                                    Contact Form
                                </h2>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                className="space-y-5"
                            >
                                {/* Honeypot spam field */}
                                <input
                                    type="checkbox"
                                    name="botcheck"
                                    className="hidden"
                                    tabIndex={-1}
                                    autoComplete="off"
                                />

                                <FormField
                                    label="Name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Your name"
                                    autoComplete="name"
                                />

                                <FormField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                />

                                <label className="block">
                                    <span className="mb-2 block text-sm font-black uppercase tracking-wide">
                                        Message
                                    </span>

                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="How can we help?"
                                        rows={7}
                                        required
                                        className="w-full resize-none rounded-[14px] border-[3px] border-black bg-[#f8f4ea] px-4 py-3 text-base font-semibold outline-none transition placeholder:text-black/40 focus:bg-white focus:shadow-[4px_4px_0_#000]"
                                    />
                                </label>

                                {status === "success" && (
                                    <div
                                        aria-live="polite"
                                        className="flex items-center gap-3 rounded-[14px] border-[3px] border-black bg-green-200 px-4 py-3 font-black shadow-[4px_4px_0_#000]"
                                    >
                                        <CheckCircle
                                            size={21}
                                            strokeWidth={3}
                                        />
                                        Message sent successfully!
                                    </div>
                                )}

                                {status === "error" && (
                                    <div
                                        aria-live="polite"
                                        className="flex items-center gap-3 rounded-[14px] border-[3px] border-black bg-red-500 px-4 py-3 font-black text-white shadow-[4px_4px_0_#000]"
                                    >
                                        <AlertCircle
                                            size={21}
                                            strokeWidth={3}
                                        />
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "loading"}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] border-[3px] border-black bg-[#ffbc00] px-6 py-4 text-base font-black uppercase tracking-wide shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000] disabled:cursor-not-allowed disabled:translate-x-[2px] disabled:translate-y-[2px] disabled:opacity-60 disabled:shadow-[2px_2px_0_#000] sm:w-auto sm:min-w-[220px]"
                                >
                                    <Send
                                        size={20}
                                        strokeWidth={3}
                                    />

                                    {status === "loading"
                                        ? "Sending..."
                                        : "Send Message"}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

function FormField({
    label,
    name,
    type,
    value,
    onChange,
    placeholder,
    autoComplete,
}) {
    return (
        <label className="block">
            <span className="mb-2 block text-sm font-black uppercase tracking-wide">
                {label}
            </span>

            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete={autoComplete}
                required
                className="h-13 w-full rounded-[14px] border-[3px] border-black bg-[#f8f4ea] px-4 py-3 text-base font-semibold outline-none transition placeholder:text-black/40 focus:bg-white focus:shadow-[4px_4px_0_#000]"
            />
        </label>
    );
}

function ContactInfoCard({
    icon: Icon,
    label,
    value,
    href,
    background,
}) {
    return (
        <a
            href={href}
            className="group flex items-center gap-4 rounded-[24px] border-[4px] border-black bg-white p-5 shadow-[7px_7px_0_#000] transition hover:-translate-y-1 hover:shadow-[9px_9px_0_#000]"
        >
            <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] border-[3px] border-black ${background}`}
            >
                <Icon size={26} strokeWidth={3} />
            </div>

            <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-500">
                    {label}
                </p>

                <p className="mt-1 break-words text-base font-black md:text-lg">
                    {value}
                </p>
            </div>
        </a>
    );
}

export default Contact;